import { I18n } from "@assets/strings";
import React, { useState } from "react";
import styled from "styled-components";
import { ChapterSummary } from "@domain/lms/course/courseContent";
import { Primary } from "@ui/components/containers/primary";
import { RoundedBlock } from "@ui/components/containers/roundedBlock";
import { ChevronDownIcon } from "@ui/components/icons";
import { gap } from "@ui/styles/utils";

interface FoldableChapterProps extends React.HTMLAttributes<HTMLDivElement> {
	courseId: string;
	chapterIndex: number;
	chapter: ChapterSummary;
}
export const FoldableChapter: React.FC<FoldableChapterProps> = ({ courseId, chapterIndex, chapter, ...htmlProps }) => {
	const [opened, setOpened] = useState(false);
	const hasSubSections = chapter.sections.length > 0;

	return (
		<Container {...htmlProps}>
			<ChapterTitle onClick={() => setOpened(!opened)} openable={hasSubSections} opened={opened}>
				<Primary>
					<span>{I18n.t("course.chapter.prefix", { index: chapterIndex + 1 })}</span>
					<b>{chapter.title}</b>
				</Primary>
				{hasSubSections && <ChevronDownIcon />}
			</ChapterTitle>
			{opened && (
				<SectionsSummary>
					{chapter.sections.map((section) => (
						<Primary key={section.id}>{section.title}</Primary>
					))}
				</SectionsSummary>
			)}
		</Container>
	);
};

const Container = styled(RoundedBlock)``;

const ChapterTitle = styled(RoundedBlock).withConfig({
	shouldForwardProp: (prop) => !["opened", "openable", "isCurrent"].includes(prop),
})<{ opened: boolean; openable: boolean }>`
	cursor: pointer;
	${({ openable }) => !openable && "pointer-events: none"};
	display: flex;
	align-items: flex-start;
	justify-content: space-between;
	padding: 15px 35px 15px 45px;
	@media (max-width: 900px) {
		padding: 15px 20px;
	}
	border-top: none;
	margin-left: -1px;
	margin-right: -1px;
	${({ opened }) => !opened && "border-bottom: none"};
	${gap(10, "row")};

	svg {
		flex-shrink: 0;
		${({ opened }) => opened && "transform: rotate(180deg)"};
	}

	a {
		pointer-events: all;
		:hover div {
			color: ${({ theme }) => theme.colors.primary};
		}
	}

	span {
		color: ${({ theme }) => theme.colors.secondary};
	}
`;

const SectionsSummary = styled.div`
	padding: 20px 45px;
	@media (max-width: 900px) {
		padding: 15px 20px;
	}
	${gap(10)};
	a  {
		display: block;
		div {
			color: ${({ theme }) => theme.colors.secondary};
		}
		:hover div {
			color: ${({ theme }) => theme.colors.primary};
		}
		&:not(:last-child) {
			padding-bottom: 10px;
			border-bottom: 1px solid ${({ theme }) => theme.colors.lightgray};
		}
	}
`;
